/** Classify live fills into entry / add (добор) / exit for short chart dashes. */
export type FillLevelKind = 'entry' | 'add' | 'exit';

export type FillLike = {
  id?: string;
  time?: number;
  price?: number;
  qty?: number;
  quantity?: number;
  side?: string;
  positionSide?: string | null;
  reduceOnly?: boolean;
  reduce_only?: boolean;
  label?: string;
};

export type LevelDash = {
  /** Unix seconds — centre of the short dash on the candle. */
  time: number;
  price: number;
  kind: FillLevelKind;
  side: 'long' | 'short' | 'exit';
  title: string;
  color: string;
};

const isExit = (f: FillLike) => Boolean(f.reduceOnly ?? f.reduce_only);
const isShort = (f: FillLike, exit: boolean) => {
  const ps = String(f.positionSide || '').toUpperCase();
  if (ps === 'SHORT') return true;
  if (ps === 'LONG') return false;
  const side = String(f.side || '').toLowerCase();
  if (exit) return side === 'buy';
  return side === 'sell';
};

const toSec = (t: unknown) => {
  const n = Number(t) || 0;
  if (!n) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
};

/** Oldest→newest short dashes at fill candles (вход / добор / выход). */
export const buildFillLevelDashes = (fills: FillLike[]): LevelDash[] => {
  const chrono = [...fills].sort((a, b) => (Number(a.time) || 0) - (Number(b.time) || 0));
  let openSide: 'long' | 'short' | null = null;
  let openCount = 0;
  const out: LevelDash[] = [];

  for (const f of chrono) {
    const price = Number(f.price);
    const time = toSec(f.time);
    if (!Number.isFinite(price) || price <= 0 || !time) continue;
    const exit = isExit(f);
    const short = isShort(f, exit);

    if (exit) {
      out.push({
        time,
        price,
        kind: 'exit',
        side: 'exit',
        title: short || openSide === 'short' ? 'out S' : 'out L',
        color: '#c9a227',
      });
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) openSide = null;
      continue;
    }

    const side = short ? 'short' : 'long';
    const kind: FillLevelKind = openSide === side && openCount > 0 ? 'add' : 'entry';
    if (openSide !== side) {
      openSide = side;
      openCount = 1;
    } else {
      openCount += 1;
    }
    out.push({
      time,
      price,
      kind,
      side,
      title: kind === 'entry' ? (side === 'long' ? 'in L' : 'in S') : `+${openCount - 1}`,
      // Soft cyan / peach — not candle green/red (bars stay readable under ticks).
      color: side === 'long' ? '#6ec8ff' : '#f0a070',
    });
  }
  return out;
};

/**
 * FIFO realized PnL:
 * - realizedPnl — USDT
 * - realizedPnlPct — % of position move = PnL / entry notional × 100
 */
export const attachRealizedPnl = <T extends FillLike>(
  fills: T[],
): Array<T & { realizedPnl: number | null; realizedPnlPct: number | null }> => {
  const chrono = [...fills]
    .map((f, idx) => ({ f, idx, t: Number(f.time) || 0 }))
    .sort((a, b) => a.t - b.t || a.idx - b.idx);

  type Lot = { side: 'long' | 'short'; qty: number; price: number };
  const lots: Lot[] = [];
  const pnlByIdx = new Map<number, { pnl: number; pct: number }>();

  for (const { f, idx } of chrono) {
    const qty = Math.abs(Number(f.qty ?? f.quantity) || 0);
    const price = Number(f.price) || 0;
    if (!(qty > 0) || !(price > 0)) continue;
    const exit = isExit(f);
    if (!exit) {
      lots.push({ side: isShort(f, false) ? 'short' : 'long', qty, price });
      continue;
    }
    let remain = qty;
    let pnl = 0;
    let entryNotional = 0;
    while (remain > 1e-12 && lots.length) {
      const lot = lots[0];
      const take = Math.min(remain, lot.qty);
      const dir = lot.side === 'long' ? 1 : -1;
      pnl += (price - lot.price) * take * dir;
      entryNotional += lot.price * take;
      lot.qty -= take;
      remain -= take;
      if (lot.qty <= 1e-12) lots.shift();
    }
    const pct = entryNotional > 0 ? (pnl / entryNotional) * 100 : NaN;
    pnlByIdx.set(idx, { pnl, pct });
  }

  return fills.map((f, idx) => {
    const v = pnlByIdx.get(idx);
    return {
      ...f,
      realizedPnl: v && Number.isFinite(v.pnl) ? v.pnl : null,
      realizedPnlPct: v && Number.isFinite(v.pct) ? v.pct : null,
    };
  });
};
